import * as React from 'react'
import { POIDocumentType } from '@/api/Clients/Types/DocumentType'
import { DocumentRequestType } from '@/api/Documents/Types/DocumentRequests'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { otherDocumentRequestTypeList } from '@/utils/documents/documentRequestTypes'
import { hasPermission } from '@/utils/permissionsValidator'
import { CheckIcon, IdCard, LogsIcon, ScrollText } from 'lucide-react'

import { ClientDetailsDashboardFormProps } from './ClientDetailsDashboardForm'

interface MoreMenuProps extends ClientDetailsDashboardFormProps {
  onDocumentRequestModal(value: ModalProps): void
}

interface ModalProps {
  visible: boolean
  request?: string
  action?: () => void
}

export const ClientDetailsMoreMenu: React.FC<MoreMenuProps> = (props) => {
  const {
    data,
    show2FA,
    showCreateLead,
    permissions,
    resetFailedLogins,
    resetForgotPasswordAttempts,
    setAccessLogModal,
    setAuditLogModal,
    setTwoFactorModal,
    createLead,
    assignAffiliate,
    verifyClientModal,
    handleIdentityVerification,
    setVerificationModal,
    setWorldCheckModal,
    setWorldCheckHistoryModal,
    setNCIHistoryModal,
    reCalculateNCI,
    syncHubspot,
    sendClientRequest,
    onDocumentRequestModal,
    sendClientOtherRequest,
    showForceLogoutModalConfirmation,
  } = props

  return (
    <>
      <DropdownMenuItem
        disabled={
          data.status.id === 1 ||
          data.status.id === 4 ||
          !hasPermission(permissions, PermissionsEnum.CanActivateClient)
        }
        onSelect={() => verifyClientModal()}
      >
        <CheckIcon />
        Activate Client
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanVerifyClientIdentity) &&
            !hasPermission(permissions, PermissionsEnum.CanViewClientIdentityVerificationResults)
          }
        >
          Identity Verification (eID)
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>
          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanVerifyClientIdentity)}
            onSelect={() => handleIdentityVerification()}
          >
            <IdCard />
            Verify
          </DropdownMenuItem>

          <DropdownMenuItem
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanViewClientIdentityVerificationResults)
            }
            onSelect={() => setVerificationModal(true)}
          >
            Reports
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening) &&
            !hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)
          }
        >
          WorldCheck
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>
          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening)}
            onSelect={() => setWorldCheckModal({ visible: true, enableOngoingScreening: false })}
          >
            Screening
          </DropdownMenuItem>

          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening)}
            onSelect={() => setWorldCheckModal({ visible: true, enableOngoingScreening: true })}
          >
            Ongoing Screening
          </DropdownMenuItem>

          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults)}
            onSelect={() => setWorldCheckHistoryModal(true)}
          >
            History
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={!hasPermission(permissions, PermissionsEnum.CanViewClientNCIInformation)}
        >
          NCI
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>
          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanChangeClientNCIInformation)}
            onSelect={() => setNCIHistoryModal({ visible: true, showAddForm: true })}
          >
            Add Priority
          </DropdownMenuItem>

          <DropdownMenuItem
            disabled={!hasPermission(permissions, PermissionsEnum.CanViewClientNCIInformation)}
            onSelect={() => setNCIHistoryModal({ visible: true })}
          >
            View Priorities
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => reCalculateNCI()}>Generate NCI</DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger>HubSpot</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem onSelect={() => syncHubspot()}>Sync</DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={!hasPermission(permissions, PermissionsEnum.CanRequestEmailClientDocuments)}
        >
          Requests
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>
          <DropdownMenuItem
            onSelect={() =>
              onDocumentRequestModal({
                visible: true,
                action: () => sendClientRequest(POIDocumentType.NationalInsuranceNumber),
                request: 'National Insurance Number',
              })
            }
          >
            National Insurance Number
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() =>
              onDocumentRequestModal({
                visible: true,
                action: () => sendClientRequest(POIDocumentType.UsCitizenClassification),
                request: 'US Citizen Classification',
              })
            }
          >
            US Citizen Classification
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() =>
              onDocumentRequestModal({
                visible: true,
                action: () => sendClientRequest(POIDocumentType.BelgiumEmail),
                request: 'Belgium Email',
              })
            }
          >
            Belgium Email
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() =>
              onDocumentRequestModal({
                visible: true,
                action: () => sendClientOtherRequest(DocumentRequestType.RequestCountryMismatch),
                request: 'Phone number clarification (country mismatch)',
              })
            }
          >
            Phone number clarification (country mismatch)
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() =>
              onDocumentRequestModal({
                visible: true,
                action: () => sendClientOtherRequest(DocumentRequestType.RequestInvalidPhoneNumber),
                request: 'Phone number clarification (invalid phone number)',
              })
            }
          >
            Phone number clarification (invalid phone number)
          </DropdownMenuItem>

          {otherDocumentRequestTypeList.client.map((requestType) => (
            <DropdownMenuItem
              key={requestType.value}
              onSelect={() =>
                onDocumentRequestModal({
                  visible: true,
                  action: () => sendClientOtherRequest(requestType.value),
                  request: requestType.label,
                })
              }
            >
              {requestType.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSeparator />

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanResetClientFailedLogins)}
        onSelect={() => resetFailedLogins()}
      >
        Reset Failed Logins
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanResetClientPasswordResetAttempts)}
        onSelect={() => resetForgotPasswordAttempts()}
      >
        Reset Forgot Password Attempts
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanViewClientAccessLog)}
        onSelect={() => setAccessLogModal(true)}
      >
        <LogsIcon />
        Access Log
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanViewClientAuditTrail)}
        onSelect={() => setAuditLogModal(true)}
        data-test='audit-log-menu-button'
      >
        <ScrollText />
        Audit Log
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      {show2FA && (
        <DropdownMenuItem onSelect={() => setTwoFactorModal()} data-test='2fa-log-menu-button'>
          2FA
        </DropdownMenuItem>
      )}

      {showCreateLead && (
        <DropdownMenuItem onSelect={() => createLead()} data-test='create-lead-menu-button'>
          Create Lead
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanAssignAffiliates)}
        onSelect={() => assignAffiliate()}
        data-test='assign-afiiliate-menu-button'
      >
        Assign Affiliate
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanForceLogoutClient)}
        onSelect={() => showForceLogoutModalConfirmation()}
        data-test='force-logout-menu-button'
      >
        Force Logout
      </DropdownMenuItem>

      {/*<DropdownMenuSeparator />*/}

      {/*<DropdownMenuItem*/}
      {/*  disabled={!hasPermission(permissions, PermissionsEnum.CanImpersonate)}*/}
      {/*  onSelect={() => onCALogin()}*/}
      {/*  data-test='ca-login-menu-button'*/}
      {/*>*/}
      {/*  CA Login*/}
      {/*</DropdownMenuItem>*/}
    </>
  )
}
